import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Avatar,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
} from '@mui/material';
import EventIcon from '@mui/icons-material/Event';
import HistoryIcon from '@mui/icons-material/History';
import PersonIcon from '@mui/icons-material/Person';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import LogoutIcon from '@mui/icons-material/Logout';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SpaIcon from '@mui/icons-material/Spa';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import AddIcon from '@mui/icons-material/Add';
import CancelIcon from '@mui/icons-material/Cancel';
import EditIcon from '@mui/icons-material/Edit';
import WarningIcon from '@mui/icons-material/Warning';
import CloseIcon from '@mui/icons-material/Close';
import LoyaltyIcon from '@mui/icons-material/Loyalty';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { customerAuthAPI, loyaltyAPI, timeSlotsAPI } from '../utils/api';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import FiveDayCalendar from '../components/FiveDayCalendar';

function CustomerPortal({ onLogout }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [profile, setProfile] = useState(null);
  const [upcomingBookings, setUpcomingBookings] = useState([]);
  const [recentBookings, setRecentBookings] = useState([]);

  // Loyalty state
  const [loyalty, setLoyalty] = useState(null);
  const [loyaltyLoading, setLoyaltyLoading] = useState(false);
  const [redeemLoading, setRedeemLoading] = useState(false);

  // Modal states
  const [cancelModal, setCancelModal] = useState({ open: false, booking: null });
  const [amendModal, setAmendModal] = useState({ open: false, booking: null });
  const [submitting, setSubmitting] = useState(false);

  // Form states
  const [cancelReason, setCancelReason] = useState('');
  const [amendData, setAmendData] = useState({ reason: '', date: '', time: '' });
  const [availableSlots, setAvailableSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [calendarStartDate, setCalendarStartDate] = useState(() => dayjs().format('YYYY-MM-DD'));

  useEffect(() => {
    fetchProfile();
    fetchLoyalty();
  }, []);

  const fetchLoyalty = async () => {
    setLoyaltyLoading(true);
    try {
      const response = await loyaltyAPI.getMyStatus();
      setLoyalty(response.data);
    } catch (err) {
      console.error('Failed to fetch loyalty status:', err);
      // Don't show error - loyalty is optional feature
    } finally {
      setLoyaltyLoading(false);
    }
  };

  const handleRedeemReward = async (category) => {
    setRedeemLoading(category);
    try {
      const response = await loyaltyAPI.redeemReward(category);
      toast.success(response.data.message);
      fetchLoyalty(); // Refresh loyalty data
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to redeem reward');
    } finally {
      setRedeemLoading(false);
    }
  };

  const copyCodeToClipboard = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Discount code copied!');
  };

  const fetchProfile = async () => {
    try {
      const response = await customerAuthAPI.getProfile();
      setProfile(response.data.customer);
      setUpcomingBookings(response.data.upcomingBookings || []);
      setRecentBookings(response.data.recentBookings || []);
    } catch (err) {
      if (err.response?.status === 401) {
        handleLogout();
      } else {
        setError('Failed to load profile');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('customerToken');
    localStorage.removeItem('customer');
    if (onLogout) onLogout();
    navigate('/portal/login');
  };

  // Fetch available slots for amend modal
  const fetchAvailableSlots = async (date, duration) => {
    if (!date) return;
    setSlotsLoading(true);
    setAvailableSlots([]);
    try {
      const response = await timeSlotsAPI.getAvailable(date, duration);
      setAvailableSlots(response.data || []);
    } catch (err) {
      console.error('Failed to fetch available slots:', err);
      toast.error('Failed to load available times');
    } finally {
      setSlotsLoading(false);
    }
  };

  // Handle date change in amend modal
  const handleAmendDateChange = (newDate) => {
    const dateStr = newDate ? newDate.format('YYYY-MM-DD') : '';
    setAmendData({ ...amendData, date: dateStr, time: '' }); // Reset time when date changes
    if (dateStr && amendModal.booking) {
      fetchAvailableSlots(dateStr, amendModal.booking.duration);
    }
  };

  const formatDate = (dateStr) => {
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-GB', {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'success';
      case 'completed': return 'success';
      case 'pending': return 'warning';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'confirmed':
      case 'completed':
        return <CheckCircleIcon color="success" />;
      case 'pending':
        return <HourglassEmptyIcon color="warning" />;
      default:
        return <EventIcon color="action" />;
    }
  };

  // Calculate hours until booking
  const getHoursUntil = (booking) => {
    const bookingDateTime = new Date(`${booking.booking_date}T${booking.start_time}`);
    const now = new Date();
    const diffMs = bookingDateTime - now;
    return Math.floor(diffMs / (1000 * 60 * 60));
  };

  // Get warning message based on notice period
  const getWarningMessage = (hoursUntil) => {
    if (hoursUntil < 24) {
      return {
        severity: 'error',
        message: 'This is less than 24 hours notice. A cancellation/change fee may apply.'
      };
    } else if (hoursUntil < 48) {
      return {
        severity: 'warning',
        message: 'This is less than 48 hours notice. Please note our cancellation policy.'
      };
    }
    return null;
  };

  // Handle cancellation request
  const handleCancelRequest = async () => {
    if (!cancelModal.booking) return;

    setSubmitting(true);
    try {
      await customerAuthAPI.submitBookingRequest({
        bookingId: cancelModal.booking.id,
        requestType: 'cancellation',
        reason: cancelReason
      });
      toast.success('Cancellation request submitted. Awaiting admin approval.');
      setCancelModal({ open: false, booking: null });
      setCancelReason('');
      fetchProfile();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle amendment request
  const handleAmendRequest = async () => {
    if (!amendModal.booking || !amendData.date || !amendData.time) {
      toast.error('Please select a new date and time');
      return;
    }

    setSubmitting(true);
    try {
      await customerAuthAPI.submitBookingRequest({
        bookingId: amendModal.booking.id,
        requestType: 'amendment',
        reason: amendData.reason,
        requestedDate: amendData.date,
        requestedTime: amendData.time
      });
      toast.success('Amendment request submitted. Awaiting admin approval.');
      setAmendModal({ open: false, booking: null });
      setAmendData({ reason: '', date: '', time: '' });
      setAvailableSlots([]);
      fetchProfile();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#FAF8F5'
        }}
      >
        <CircularProgress color="primary" />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
        <Button onClick={fetchProfile} sx={{ mt: 2 }}>
          Try Again
        </Button>
      </Container>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', background: '#FAF8F5', pb: 4 }}>
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          background: 'linear-gradient(135deg, #8B2635 0%, #A84454 100%)',
          color: 'white',
          py: 3,
          px: 2,
          mb: 3,
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar
                sx={{
                  width: 56,
                  height: 56,
                  bgcolor: 'rgba(255,255,255,0.2)',
                  fontSize: '1.5rem',
                  fontWeight: 600
                }}
              >
                {profile?.name?.charAt(0)?.toUpperCase() || 'C'}
              </Avatar>
              <Box>
                <Typography variant="h5" fontWeight={600}>
                  Welcome back, {profile?.name?.split(' ')[0]}!
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  {profile?.email}
                </Typography>
              </Box>
            </Box>
            <Button
              variant="outlined"
              startIcon={<LogoutIcon />}
              onClick={handleLogout}
              sx={{
                color: 'white',
                borderColor: 'rgba(255,255,255,0.5)',
                '&:hover': {
                  borderColor: 'white',
                  background: 'rgba(255,255,255,0.1)'
                }
              }}
            >
              Logout
            </Button>
          </Box>
        </Container>
      </Paper>

      <Container maxWidth="lg">
        <Grid container spacing={3}>
          {/* Quick Actions */}
          <Grid item xs={12}>
            <Button
              component={Link}
              to="/"
              variant="contained"
              size="large"
              startIcon={<AddIcon />}
            >
              Book New Appointment
            </Button>
          </Grid>

          {/* Stats Cards */}
          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <EventIcon sx={{ fontSize: 32, color: 'primary.main', mb: 0.5 }} />
                <Typography variant="h5" fontWeight={700} color="primary.main">
                  {upcomingBookings.length}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Upcoming
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <HistoryIcon sx={{ fontSize: 32, color: 'secondary.main', mb: 0.5 }} />
                <Typography variant="h5" fontWeight={700} color="secondary.dark">
                  {profile?.totalVisits || 0}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Total Visits
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <CreditCardIcon sx={{ fontSize: 32, color: profile?.hasCardOnFile ? 'success.main' : 'grey.400', mb: 0.5 }} />
                <Typography variant="body2" fontWeight={600} color={profile?.hasCardOnFile ? 'success.main' : 'text.secondary'}>
                  {profile?.hasCardOnFile ? 'Saved' : 'None'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Card on File
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <PersonIcon sx={{ fontSize: 32, color: 'info.main', mb: 0.5 }} />
                <Typography variant="body2" fontWeight={600} color="text.primary">
                  {profile?.memberSince ? new Date(profile.memberSince).getFullYear() : '-'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Member Since
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Loyalty Stamp Cards - Per Service Category */}
          {loyalty?.stampCards && loyalty.stampCards.length > 0 && (
            <Grid item xs={12}>
              <Card sx={{
                background: 'linear-gradient(135deg, #8B2635 0%, #A84454 100%)',
                color: 'white',
                overflow: 'visible'
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <LoyaltyIcon sx={{ mr: 1 }} />
                    <Typography variant="h6" fontWeight={600}>
                      Loyalty Rewards
                    </Typography>
                  </Box>

                  <Typography variant="body2" sx={{ mb: 2, opacity: 0.9 }}>
                    Collect {loyalty.stampCards[0]?.stampsNeeded || 6} stamps in each category for {loyalty.discountPercent}% off!
                  </Typography>

                  {/* Active Discount Codes */}
                  {loyalty.activeCodes && loyalty.activeCodes.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      {loyalty.activeCodes.map((code, idx) => (
                        <Box
                          key={idx}
                          sx={{
                            bgcolor: '#D4A574',
                            color: '#1A1A1A',
                            borderRadius: 2,
                            p: 2,
                            mb: 1
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                            <CardGiftcardIcon sx={{ mr: 1, fontSize: 18 }} />
                            <Typography variant="subtitle2" fontWeight={600}>
                              {code.category} - {code.discount}% Off
                            </Typography>
                          </Box>
                          <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            bgcolor: 'rgba(0,0,0,0.1)',
                            borderRadius: 1,
                            px: 2,
                            py: 0.5
                          }}>
                            <Typography fontWeight={700} fontFamily="monospace">
                              {code.code}
                            </Typography>
                            <IconButton
                              size="small"
                              onClick={() => copyCodeToClipboard(code.code)}
                              sx={{ color: '#1A1A1A' }}
                            >
                              <ContentCopyIcon fontSize="small" />
                            </IconButton>
                          </Box>
                          <Typography variant="caption">
                            Expires: {new Date(code.expiresAt).toLocaleDateString()}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}

                  {/* Stamp Cards Grid */}
                  <Grid container spacing={2}>
                    {loyalty.stampCards.map((card) => {
                      const hasActiveCode = loyalty.activeCodes?.some(c => c.category === card.category);
                      return (
                        <Grid item xs={12} sm={6} md={4} key={card.category}>
                          <Box sx={{
                            bgcolor: 'rgba(255,255,255,0.1)',
                            borderRadius: 2,
                            p: 2
                          }}>
                            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                              {card.category}
                            </Typography>

                            {/* Stamp circles */}
                            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.75, mb: 1 }}>
                              {[...Array(card.stampsNeeded)].map((_, index) => (
                                <Box
                                  key={index}
                                  sx={{
                                    width: 28,
                                    height: 28,
                                    borderRadius: '50%',
                                    border: '2px solid rgba(255,255,255,0.4)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    bgcolor: index < card.stamps ? 'rgba(255,255,255,0.2)' : 'transparent'
                                  }}
                                >
                                  {index < card.stamps ? (
                                    <StarIcon sx={{ color: '#D4A574', fontSize: 18 }} />
                                  ) : (
                                    <StarBorderIcon sx={{ color: 'rgba(255,255,255,0.3)', fontSize: 18 }} />
                                  )}
                                </Box>
                              ))}
                            </Box>

                            <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', opacity: 0.8, mb: 1 }}>
                              {card.stamps >= card.stampsNeeded ? (
                                <strong>Reward ready!</strong>
                              ) : (
                                <>{card.stampsToReward} more to reward</>
                              )}
                            </Typography>

                            {/* Redeem button for this category */}
                            {card.rewardsAvailable > 0 && !hasActiveCode && (
                              <Button
                                fullWidth
                                size="small"
                                variant="contained"
                                onClick={() => handleRedeemReward(card.category)}
                                disabled={redeemLoading === card.category}
                                sx={{
                                  bgcolor: '#D4A574',
                                  color: '#1A1A1A',
                                  fontSize: '0.75rem',
                                  py: 0.5,
                                  '&:hover': { bgcolor: '#E8C9A5' }
                                }}
                              >
                                {redeemLoading === card.category ? 'Redeeming...' : 'Claim Reward'}
                              </Button>
                            )}
                          </Box>
                        </Grid>
                      );
                    })}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Upcoming Appointments */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <EventIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6" fontWeight={600}>
                    Upcoming Appointments
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />

                {upcomingBookings.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography color="text.secondary" gutterBottom>
                      No upcoming appointments
                    </Typography>
                    <Button
                      component={Link}
                      to="/"
                      variant="outlined"
                      startIcon={<AddIcon />}
                      sx={{ mt: 1 }}
                    >
                      Book Now
                    </Button>
                  </Box>
                ) : (
                  <List disablePadding>
                    {upcomingBookings.map((booking, index) => (
                      <React.Fragment key={booking.id}>
                        {index > 0 && <Divider />}
                        <ListItem
                          sx={{ px: 0, py: 2, flexDirection: 'column', alignItems: 'stretch' }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%', mb: 1 }}>
                            <ListItemIcon sx={{ minWidth: 40, mt: 0.5 }}>
                              {getStatusIcon(booking.status)}
                            </ListItemIcon>
                            <ListItemText
                              primary={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <SpaIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                                  <Typography fontWeight={600}>
                                    {booking.service_name}
                                  </Typography>
                                </Box>
                              }
                              secondary={
                                <Box sx={{ mt: 0.5 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
                                    <CalendarTodayIcon sx={{ fontSize: 14 }} />
                                    <Typography variant="body2">
                                      {formatDate(booking.booking_date)}
                                    </Typography>
                                  </Box>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
                                    <AccessTimeIcon sx={{ fontSize: 14 }} />
                                    <Typography variant="body2">
                                      {booking.start_time}
                                    </Typography>
                                  </Box>
                                </Box>
                              }
                            />
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-end' }}>
                              <Chip
                                label={booking.status}
                                color={getStatusColor(booking.status)}
                                size="small"
                                sx={{ textTransform: 'capitalize' }}
                              />
                              {/* Show pending request status */}
                              {booking.request_type && booking.request_status === 'pending' && (
                                <Chip
                                  icon={<HourglassEmptyIcon sx={{ fontSize: 14 }} />}
                                  label={booking.request_type === 'cancellation' ? 'Cancellation Requested' : 'Change Requested'}
                                  color="warning"
                                  size="small"
                                  variant="outlined"
                                  sx={{ fontSize: '0.7rem' }}
                                />
                              )}
                            </Box>
                          </Box>

                          {/* Action buttons - hide if pending request exists */}
                          {booking.status !== 'cancelled' && !booking.request_type && (
                            <Box sx={{ display: 'flex', gap: 1, ml: 5 }}>
                              <Button
                                size="small"
                                variant="outlined"
                                color="primary"
                                startIcon={<EditIcon />}
                                onClick={() => {
                                  setAmendData({ reason: '', date: '', time: '' });
                                  setAvailableSlots([]);
                                  setAmendModal({ open: true, booking });
                                }}
                              >
                                Request Change
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                color="error"
                                startIcon={<CancelIcon />}
                                onClick={() => {
                                  setCancelReason('');
                                  setCancelModal({ open: true, booking });
                                }}
                              >
                                Request Cancel
                              </Button>
                            </Box>
                          )}
                          {/* Show message when request is pending */}
                          {booking.request_type && booking.request_status === 'pending' && (
                            <Box sx={{ ml: 5 }}>
                              <Typography variant="caption" color="text.secondary">
                                Your request is being reviewed. We'll notify you once it's processed.
                              </Typography>
                            </Box>
                          )}
                        </ListItem>
                      </React.Fragment>
                    ))}
                  </List>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Recent Appointments */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <HistoryIcon color="secondary" sx={{ mr: 1 }} />
                  <Typography variant="h6" fontWeight={600}>
                    Recent Appointments
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />

                {recentBookings.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography color="text.secondary">
                      No previous appointments
                    </Typography>
                  </Box>
                ) : (
                  <List disablePadding>
                    {recentBookings.slice(0, 5).map((booking, index) => (
                      <React.Fragment key={booking.id}>
                        {index > 0 && <Divider />}
                        <ListItem sx={{ px: 0, py: 1.5 }}>
                          <ListItemText
                            primary={
                              <Typography fontWeight={500}>
                                {booking.service_name}
                              </Typography>
                            }
                            secondary={formatDate(booking.booking_date)}
                          />
                          <Box sx={{ textAlign: 'right' }}>
                            <Typography fontWeight={600} color="primary.main">
                              £{booking.service_price}
                            </Typography>
                            <Chip
                              label={booking.status}
                              color={getStatusColor(booking.status)}
                              size="small"
                              sx={{ textTransform: 'capitalize', mt: 0.5 }}
                            />
                          </Box>
                        </ListItem>
                      </React.Fragment>
                    ))}
                  </List>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>

      {/* Cancel Request Modal */}
      <Dialog
        open={cancelModal.open}
        onClose={() => !submitting && setCancelModal({ open: false, booking: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            Request Cancellation
            <IconButton
              onClick={() => setCancelModal({ open: false, booking: null })}
              disabled={submitting}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {cancelModal.booking && (
            <>
              {/* Warning if short notice */}
              {(() => {
                const hoursUntil = getHoursUntil(cancelModal.booking);
                const warning = getWarningMessage(hoursUntil);
                if (warning) {
                  return (
                    <Alert severity={warning.severity} icon={<WarningIcon />} sx={{ mb: 2 }}>
                      {warning.message}
                    </Alert>
                  );
                }
                return null;
              })()}

              <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="subtitle2" color="text.secondary">Booking Details</Typography>
                <Typography fontWeight={600}>{cancelModal.booking.service_name}</Typography>
                <Typography variant="body2">
                  {formatDate(cancelModal.booking.booking_date)} at {cancelModal.booking.start_time}
                </Typography>
              </Box>

              <TextField
                fullWidth
                label="Reason for cancellation"
                placeholder="Please tell us why you need to cancel..."
                multiline
                rows={3}
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                helperText="This helps us understand and improve our service"
              />
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setCancelModal({ open: false, booking: null })}
            disabled={submitting}
          >
            Keep Booking
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleCancelRequest}
            disabled={submitting}
          >
            {submitting ? <CircularProgress size={20} /> : 'Submit Request'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Amendment Request Modal */}
      <Dialog
        open={amendModal.open}
        onClose={() => !submitting && setAmendModal({ open: false, booking: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            Request Date/Time Change
            <IconButton
              onClick={() => setAmendModal({ open: false, booking: null })}
              disabled={submitting}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {amendModal.booking && (
            <>
              {/* Warning if short notice */}
              {(() => {
                const hoursUntil = getHoursUntil(amendModal.booking);
                const warning = getWarningMessage(hoursUntil);
                if (warning) {
                  return (
                    <Alert severity={warning.severity} icon={<WarningIcon />} sx={{ mb: 2 }}>
                      {warning.message}
                    </Alert>
                  );
                }
                return null;
              })()}

              <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="subtitle2" color="text.secondary">Current Booking</Typography>
                <Typography fontWeight={600}>{amendModal.booking.service_name}</Typography>
                <Typography variant="body2">
                  {formatDate(amendModal.booking.booking_date)} at {amendModal.booking.start_time}
                </Typography>
              </Box>

              <Typography variant="subtitle2" gutterBottom>
                Select New Date & Time
              </Typography>

              {/* Calendar for date selection - 5-Day View */}
              <Box sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                mb: 2,
                p: 2,
              }}>
                <FiveDayCalendar
                  selectedDate={amendData.date}
                  onDateSelect={(dateStr) => {
                    setAmendData({ ...amendData, date: dateStr, time: '' });
                    if (dateStr && amendModal.booking) {
                      fetchAvailableSlots(dateStr, amendModal.booking.duration);
                    }
                  }}
                  startDate={calendarStartDate}
                  onNavigate={(days) => setCalendarStartDate(prev => dayjs(prev).add(days, 'day').format('YYYY-MM-DD'))}
                  compact={true}
                />
              </Box>

              {/* Selected date display */}
              {amendData.date && (
                <Box sx={{
                  p: 2,
                  mb: 2,
                  bgcolor: '#FAF8F5',
                  borderRadius: 1,
                  borderLeft: '3px solid',
                  borderColor: 'primary.main',
                }}>
                  <Typography variant="caption" color="text.secondary">Selected Date</Typography>
                  <Typography fontWeight={600}>
                    {dayjs(amendData.date).format('dddd, D MMMM YYYY')}
                  </Typography>
                </Box>
              )}

              {/* Available time slots */}
              {amendData.date && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Available Times
                  </Typography>
                  {slotsLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : availableSlots.length === 0 ? (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      No available slots on this date. Please try another date.
                    </Alert>
                  ) : (
                    <Box sx={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
                      gap: 1,
                    }}>
                      {availableSlots.map((slot, index) => (
                        <Box
                          key={index}
                          onClick={() => setAmendData({ ...amendData, time: slot.start_time })}
                          sx={{
                            p: 1.5,
                            textAlign: 'center',
                            borderRadius: 1,
                            cursor: 'pointer',
                            border: '1px solid',
                            borderColor: amendData.time === slot.start_time ? 'primary.main' : 'divider',
                            bgcolor: amendData.time === slot.start_time ? 'primary.main' : 'background.paper',
                            color: amendData.time === slot.start_time ? 'white' : 'text.primary',
                            fontWeight: amendData.time === slot.start_time ? 600 : 400,
                            transition: 'all 0.2s',
                            '&:hover': {
                              borderColor: 'primary.main',
                              bgcolor: amendData.time === slot.start_time ? 'primary.main' : 'primary.50',
                            },
                          }}
                        >
                          <Typography variant="body2" fontWeight="inherit">
                            {slot.start_time}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              )}

              <TextField
                fullWidth
                label="Reason for change"
                placeholder="Please tell us why you need to change the date/time..."
                multiline
                rows={2}
                value={amendData.reason}
                onChange={(e) => setAmendData({ ...amendData, reason: e.target.value })}
              />

              {amendData.time && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  Your change request will be reviewed and confirmed as soon as possible.
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setAmendModal({ open: false, booking: null })}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAmendRequest}
            disabled={submitting || !amendData.date || !amendData.time}
          >
            {submitting ? <CircularProgress size={20} /> : 'Submit Request'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default CustomerPortal;
